<template>
    <div v-if="runtimeStore.getCRMOption('asp.option.disable.invoice.module')">
        <div class="text-subtitle-1 font-weight-medium pb-3">{{ $t('invoicing_rules') }}</div>
        <v-radio-group v-model="formData.manualInvoicesForFullAmount">
            <v-radio
                :value="false"
                :label="$t('invoicing_rules_without_commission')"
                data-cy="invoicing-without-commission"
            />
            <v-radio :value="true" :label="$t('invoicing_rules_with_commission')" data-cy="invoicing-with-commission" />
        </v-radio-group>
    </div>
    <div v-else>
        <v-checkbox
            v-model="formData.enableRequestInvoicesForCustomer"
            :label="$t('enable_request_invoices_for_customer')"
            data-cy="enable-request-invoices-for-customer"
            hide-details
            @update:model-value="resetValues"
        />
        <div class="pl-8">
            <div class="text-subtitle-1 pb-1">{{ $t('enable_request_invoices_for_customer_note') }}</div>
            <div class="text-subtitle-1 font-weight-medium">{{ $t('invoicing_rules') }}</div>
            <v-radio-group
                v-model="formData.enableRequestInvoicesForAllServices"
                :disabled="!formData.enableRequestInvoicesForCustomer || isReadOnly"
                hide-details
            >
                <v-radio
                    :value="false"
                    :label="$t('invoices_for_separate_services')"
                    data-cy="invoices-for-separate-services"
                />
                <v-radio :value="true" :label="$t('invoices_for_all_services')" data-cy="invoices-for-all-services" />
            </v-radio-group>
        </div>

        <template
            v-if="runtimeStore.getCRMOption('asp.option.invoice.partial') && formData.enableRequestInvoicesForCustomer"
        >
            <v-checkbox
                v-model="formData.enablePartialInvoiceForCustomer"
                :label="$t('allow_the_client_to_request_an_invoice')"
                data-cy="allow-the-client-to-request-an-invoice"
                hide-details
            />
            <div class="text-subtitle-1 pb-1 pl-8">{{ $t('allow_the_client_to_request_an_invoice_note') }}</div>
        </template>

        <template v-if="runtimeStore.getCRMOption('asp.option.finance.event.rates.configuration.enabled')">
            <v-checkbox
                v-model="formData.applyCustomRatesSettings"
                :label="$t('apply_custom_currency_recalculation_rules')"
                data-cy="apply-custom-currency-recalculation-rules"
                hide-details
                @update:model-value="updateCustomRatesSettings"
            />
            <div class="text-subtitle-1 pb-1 pl-8">{{ $t('apply_custom_currency_recalculation_rules_note') }}</div>
        </template>

        <v-checkbox
            v-model="formData.enableEditVoucherAfterInvoicing"
            :label="$t('allow_to_edit_cancel_services')"
            data-cy="allow-to-edit-cancel-services"
            hide-details
            :disabled="!formData.enableRequestInvoicesForCustomer || isReadOnly"
        />
        <div v-if="!formData.enableRequestInvoicesForCustomer" class="text-subtitle-1 text-error pb-1 pl-8">
            *{{ $t('allow_to_edit_cancel_services_error_note') }}
        </div>
        <div class="text-subtitle-1 pb-1 pl-8">{{ $t('allow_to_edit_cancel_services_note') }}</div>

        <template
            v-if="
                runtimeStore.getCRMOption('asp.option.finance.event.rates.configuration.enabled') &&
                formData.applyCustomRatesSettings
            "
        >
            <div class="text-subtitle-1 font-weight-medium pt-4">{{ $t('currency_recalculation_rules') }}</div>
            <v-row no-gutters>
                <v-col cols="12" md="6" lg="4">
                    <div class="text-subtitle-1 pb-1">{{ $t('issue_invoices_for_cancellation_fee') }}</div>
                    <v-radio-group v-model="formData.currentRateForFinanceEvent.PENALTY">
                        <v-radio
                            :value="false"
                            :label="$t('using_exchange_rate_from_the_initial_invoice')"
                            data-cy="using-exchange-rate-from-the-initial-invoice"
                        />
                        <v-radio
                            :value="true"
                            :label="$t('using_current_exchange_rate')"
                            data-cy="using-current-exchange-rate"
                        />
                    </v-radio-group>
                    <div class="text-subtitle-1 pb-1">{{ $t('calculate_payment_for_the_invoice') }}</div>
                    <v-radio-group v-model="formData.currentRateForFinanceEvent.PAYMENT">
                        <v-radio
                            :value="false"
                            :label="$t('using_exchange_rate_from_the_invoice')"
                            data-cy="using-exchange-rate-from-the-invoice"
                        />
                        <v-radio
                            :value="true"
                            :label="$t('using_current_exchange_rate')"
                            data-cy="payment-using-current-exchange-rate"
                        />
                    </v-radio-group>
                </v-col>
                <v-col cols="12" md="6" lg="4">
                    <div class="text-subtitle-1 pb-1">{{ $t('issue_credit_notes') }}</div>
                    <v-radio-group v-model="formData.currentRateForFinanceEvent.CANCEL">
                        <v-radio
                            :value="false"
                            :label="$t('using_exchange_rate_from_the_initial_invoice')"
                            data-cy="cancel-using-exchange-rate-from-the-initial-invoice"
                        />
                        <v-radio
                            :value="true"
                            :label="$t('using_current_exchange_rate')"
                            data-cy="cancel-using-current-exchange-rate"
                        />
                    </v-radio-group>
                    <div class="text-subtitle-1 pb-1">{{ $t('once_the_service_is_modified') }}</div>
                    <v-radio-group v-model="formData.currentRateForFinanceEvent.MODIFY">
                        <v-radio
                            :value="false"
                            :label="$t('using_exchange_rate_from_the_initial_reservation')"
                            data-cy="using-exchange-rate-from-the-initial-reservation"
                        />
                        <v-radio
                            :value="true"
                            :label="$t('using_current_exchange_rate')"
                            data-cy="modify-using-current-exchange-rate"
                        />
                    </v-radio-group>
                </v-col>
            </v-row>
        </template>
    </div>
</template>
<script setup lang="ts">
    const formData = defineModel<any>()
    const isReadOnly = inject('isReadOnly', ref(false))

    const resetValues = () => {
        if (!formData.value.enableRequestInvoicesForCustomer) {
            formData.value.enableRequestInvoicesForAllServices = true
            if (runtimeStore.getCRMOption('asp.option.invoice.partial')) {
                formData.value.enablePartialInvoiceForCustomer = false
            }
            formData.value.enableEditVoucherAfterInvoicing = false
        } else {
            formData.value.enableEditVoucherAfterInvoicing = true
        }
    }

    const updateCustomRatesSettings = () => {
        if (formData.value.applyCustomRatesSettings) {
            formData.value.currentRateForFinanceEvent = {
                PAYMENT: true,
                PENALTY: true,
                MODIFY: true,
                CANCEL: true,
            }
        } else {
            formData.value.currentRateForFinanceEvent = {
                PAYMENT: false,
                PENALTY: false,
                MODIFY: false,
                CANCEL: false,
            }
        }
    }
</script>
